import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { MobileHeaderSlotContext } from '../../organisms/MainPage/MobileNavigation/MobileHeaderSlotContext';
import './index.scss';

/**
 * Sticky tabbar that anchor-scrolls to in-page sections and uses an
 * IntersectionObserver to highlight whichever section is currently in view.
 */
export default function SectionTabbar({
  tabs,
  activeId,
  onChange,
  leading,
  actions,
  ariaLabel,
  centerTabs = false,
  showActionsOnMobile = false,
  className = '',
}) {
  const tabsContainerRef = useRef(null);
  const tabRefs = useRef(new Map());
  // On mobile the header exposes a slot; portal the bar into it so the tabs +
  // actions live in the header. Null on desktop / inside overlays → render in
  // place (unchanged).
  const mobileHeaderSlot = useContext(MobileHeaderSlotContext);
  const inMobileHeader = Boolean(mobileHeaderSlot);
  const tabIdSignature = tabs.map((tab) => tab.id).join('\n');

  const setTabRef = useCallback((id) => (node) => {
    if (node) {
      tabRefs.current.set(id, node);
    } else {
      tabRefs.current.delete(id);
    }
  }, []);

  useEffect(() => {
    if (!onChange || typeof window === 'undefined') return undefined;
    if (typeof IntersectionObserver === 'undefined') return undefined;
    const sectionIds = tabs.map((t) => t.id);
    const sections = sectionIds
      .map((id) => document.getElementById(id))
      .filter(Boolean);
    if (sections.length === 0) return undefined;

    const obs = new IntersectionObserver((entries) => {
      const visible = entries
        .filter((e) => e.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (visible[0]) onChange(visible[0].target.id);
    }, { rootMargin: '-80px 0px -55% 0px', threshold: 0 });

    sections.forEach((s) => obs.observe(s));
    return () => obs.disconnect();
  }, [tabs, onChange]);

  useEffect(() => {
    // Keep the active tab centered as the page scrolls and the active section
    // changes. Runs wherever the tab strip is horizontally scrollable — the
    // in-app mobile header AND the in-place preview/public pages (the
    // maxScrollLeft check below no-ops on desktop, where the tabs wrap instead
    // of scrolling).
    if (!activeId || typeof window === 'undefined') {
      return;
    }

    const container = tabsContainerRef.current;
    const activeTab = tabRefs.current.get(activeId);
    if (!container || !activeTab) {
      return;
    }

    const maxScrollLeft = container.scrollWidth - container.clientWidth;
    if (maxScrollLeft <= 0) {
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const activeRect = activeTab.getBoundingClientRect();
    const activeWidth = activeRect.width || activeTab.offsetWidth;
    const activeCenter = (activeRect.left - containerRect.left)
      + container.scrollLeft
      + (activeWidth / 2);
    const centeredLeft = activeCenter - (container.clientWidth / 2);
    const nextScrollLeft = Math.min(Math.max(centeredLeft, 0), maxScrollLeft);
    if (Math.abs(container.scrollLeft - nextScrollLeft) < 1) {
      return;
    }

    const prefersReducedMotion = typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (typeof container.scrollTo === 'function') {
      container.scrollTo({
        left: nextScrollLeft,
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
      });
      return;
    }

    container.scrollLeft = nextScrollLeft;
  }, [activeId, tabIdSignature]);

  const handleClick = (id) => (e) => {
    e.preventDefault();
    if (onChange) onChange(id);
    if (typeof document !== 'undefined') {
      const target = document.getElementById(id);
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  const classes = [
    'section-tabbar',
    centerTabs && 'section-tabbar--center-tabs',
    (showActionsOnMobile || inMobileHeader) && 'section-tabbar--show-actions-mobile',
    inMobileHeader && 'section-tabbar--in-mobile-header',
    className,
  ].filter(Boolean).join(' ');

  const nav = (
    <nav className={classes} aria-label={ariaLabel}>
      {leading ? (
        <div className="section-tabbar__leading">{leading}</div>
      ) : null}
      <div className="section-tabbar__tabs" ref={tabsContainerRef}>
        {tabs.map((tab) => {
          const isActive = tab.id === activeId;
          return (
            <a
              key={tab.id}
              ref={setTabRef(tab.id)}
              href={`#${tab.id}`}
              className={`section-tabbar__tab${isActive ? ' section-tabbar__tab--active' : ''}`}
              onClick={handleClick(tab.id)}
            >
              {tab.label}
            </a>
          );
        })}
      </div>
      {actions ? (
        <div className="section-tabbar__actions">{actions}</div>
      ) : null}
    </nav>
  );

  if (inMobileHeader) {
    return createPortal(nav, mobileHeaderSlot);
  }
  return nav;
}

SectionTabbar.propTypes = {
  tabs: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.node.isRequired,
  })).isRequired,
  activeId: PropTypes.string,
  onChange: PropTypes.func,
  leading: PropTypes.node,
  actions: PropTypes.node,
  ariaLabel: PropTypes.string,
  centerTabs: PropTypes.bool,
  showActionsOnMobile: PropTypes.bool,
  className: PropTypes.string,
};
